import { useState } from 'react';
import { SHAPE_LIBRARY, getShapePoints } from '../../lib/shapePoints';

interface ShapePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectShape: (shapeType: string) => void;
}

const CATEGORIES = [
  { key: 'basic', label: 'Basic' },
  { key: 'polygon', label: 'Polygons' },
  { key: 'star', label: 'Stars' },
  { key: 'arrow', label: 'Arrows' },
  { key: 'special', label: 'Special' },
] as const;

const getShapeSvgPath = (shapeType: string, size: number): string => {
  if (shapeType === 'rectangle') {
    return `M 2 2 L ${size - 2} 2 L ${size - 2} ${size - 2} L 2 ${size - 2} Z`;
  }
  if (shapeType === 'rounded-rect') {
    const r = size * 0.15;
    return `M ${r + 2} 2 L ${size - r - 2} 2 Q ${size - 2} 2 ${size - 2} ${r + 2} L ${size - 2} ${size - r - 2} Q ${size - 2} ${size - 2} ${size - r - 2} ${size - 2} L ${r + 2} ${size - 2} Q 2 ${size - 2} 2 ${size - r - 2} L 2 ${r + 2} Q 2 2 ${r + 2} 2 Z`;
  }
  if (shapeType === 'circle') {
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 2;
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
  }
  const points = getShapePoints(shapeType, size - 4, size - 4);
  if (!points || points.length === 0) return '';
  const offsetPoints = points.map((p) => ({ x: p.x + 2, y: p.y + 2 }));
  return offsetPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ') + ' Z';
};

function ShapeThumbnail({ shapeType, size = 32 }: { shapeType: string; size?: number }) {
  const path = getShapeSvgPath(shapeType, size);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function ShapePicker({ isOpen, onClose, onSelectShape }: ShapePickerProps) {
  const [activeCategory, setActiveCategory] = useState<string>('basic');

  if (!isOpen) return null;

  const filteredShapes = SHAPE_LIBRARY.filter((s) => s.category === activeCategory);

  return (
    <div
      className="absolute top-full left-0 mt-1 z-50 w-60 rounded-lg border border-white/10 bg-[#1a1a2e] shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50">Shapes</span>
        <button
          onClick={onClose}
          className="rounded p-0.5 text-white/40 hover:bg-white/10 hover:text-white/80 transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="flex gap-0.5 border-b border-white/10 px-2 py-1.5 overflow-x-auto">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setActiveCategory(cat.key)}
            className={`whitespace-nowrap rounded px-2 py-1 text-[10px] font-medium transition-colors ${
              activeCategory === cat.key
                ? 'bg-cyan-500/20 text-cyan-400'
                : 'text-white/40 hover:bg-white/10 hover:text-white/70'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-4 gap-0.5 p-2">
        {filteredShapes.map((shape) => (
          <button
            key={shape.type}
            onClick={() => {
              onSelectShape(shape.type);
              onClose();
            }}
            className="flex flex-col items-center gap-0.5 rounded-md p-1.5 text-white/50 hover:bg-white/10 hover:text-cyan-400 transition-colors"
            title={shape.label}
          >
            <ShapeThumbnail shapeType={shape.type} />
            <span className="text-[9px] leading-tight text-center truncate w-full">
              {shape.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
